import {
  Box,
  Button,
  chakra,
  ComposedCard,
  HStack,
  List,
  ListItem,
  Page,
  Skeleton,
  Stack,
  Text,
} from '@eigtech/ui-shared-dave'
import { LinkButton, Outlet, useMatches } from '@eigtech/ui-shared-router'
import { SanityClient } from '@sanity/client'
import { range } from 'lodash-es'
import { RefObject, useEffect, useRef, useState } from 'react'
import { HiArrowNarrowUp } from 'react-icons/hi'
import { useListDocumentation } from './api/sanity/getDocumentation'

export type DocumentationDashboardProps = {
  documentationDetailRoute: string
  sanityClient: SanityClient
}

export function DocumentationDashboard({
  documentationDetailRoute,
  sanityClient,
}: DocumentationDashboardProps) {
  const { data: documentation, isPending } = useListDocumentation(sanityClient)

  const matches = useMatches()

  const intersectionRef = useRef<HTMLDivElement>(null!)

  return (
    <Page containerProps={{ maxW: 'container.lg' }} heading="Documentation">
      <HStack alignItems="flex-start" position="relative" spacing="4">
        <Stack position="sticky" spacing="4" top="2">
          <ComposedCard flexShrink={0} heading="Table of Contents" w="72">
            <chakra.nav>
              <List spacing="1">
                {isPending
                  ? range(0, 3).map((i) => <Skeleton key={i} h="10" />)
                  : documentation?.map((doc) => (
                      <ListItem key={doc.name}>
                        <LinkButton
                          h="auto"
                          justifyContent="flex-start"
                          p="2"
                          params={{ slug: doc.slug } as never}
                          to={documentationDetailRoute}
                          variant="ghost"
                          w="full"
                          whiteSpace="normal"
                          wordBreak="break-word"
                        >
                          {doc.name}
                        </LinkButton>
                      </ListItem>
                    ))}
              </List>
            </chakra.nav>
          </ComposedCard>

          <ScrollToTop intersectionRef={intersectionRef} />
        </Stack>

        <Box flex={1} minW="1px">
          <Box ref={intersectionRef} h="1px" w="1px"></Box>
          {matches.length > 1 ? (
            <Outlet />
          ) : (
            <ComposedCard heading="Documentation">
              <Text>Select the documentation you&apos;d like to view to the left.</Text>
            </ComposedCard>
          )}
        </Box>
      </HStack>
    </Page>
  )
}

function ScrollToTop({ intersectionRef }: { intersectionRef: RefObject<HTMLElement> }) {
  const [entry, setEntry] = useState<IntersectionObserverEntry | null>(null)

  const observer = useRef<IntersectionObserver | null>(null)

  useEffect(() => {
    observer.current?.disconnect()

    observer.current = new IntersectionObserver(([entry]) => setEntry(entry), {
      root: null,
      rootMargin: '50px',
      threshold: 0,
    })

    const currentObserver = observer.current
    const el = intersectionRef.current

    if (!el) return

    currentObserver.observe(el)

    return () => {
      currentObserver.disconnect()
    }
  }, [intersectionRef])

  if (entry?.isIntersecting !== false) return null

  return (
    <Button
      rightIcon={<HiArrowNarrowUp />}
      variant="outline"
      onClick={() => {
        window.scroll({
          top: 0,
          behavior: 'smooth',
        })
      }}
    >
      Scroll to Top
    </Button>
  )
}
